/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as Icons from 'lucide-react';

const {
  Lightbulb = Icons.HelpCircle,
  Radio = Icons.Radio,
  ArrowRight = Icons.ArrowRight,
  Shield = Icons.Shield,
  FileText = Icons.FileText,
  Map = Icons.Map,
  Zap = Icons.Zap,
  Phone = Icons.Phone,
  Mail = Icons.Mail,
  MapPin = Icons.MapPin,
  Menu = Icons.Menu,
  Award = Icons.Award,
  TrendingUp = Icons.TrendingUp,
  CheckCircle2 = Icons.CheckCircle2,
  Cpu = Icons.Cpu,
  Globe2 = Icons.Globe2,
  MessageCircle = Icons.MessageCircle,
  ChevronDown = Icons.ChevronDown,
  Clock = Icons.Clock,
  DollarSign = Icons.DollarSign,
  Activity = Icons.Activity,
  Database = Icons.Database,
  Cloud = Icons.Cloud,
  Headphones = Icons.Headphones,
  BookMarked = Icons.BookMarked,
  X = Icons.X,
  Receipt = Icons.Receipt,
  GitMerge = Icons.GitMerge,
  ScanLine = Icons.ScanLine,
  FilePlus = Icons.FilePlus,
  MapPinned = Icons.MapPin,
  BarChart = Icons.BarChart,
  FileSearch = Icons.FileSearch,
  CheckSquare = Icons.CheckSquare,
  Coins = Icons.Coins,
  Star = Icons.Star,
  Briefcase = Icons.Briefcase,
  HelpCircle = Icons.HelpCircle,
  RefreshCw = Icons.RefreshCw
} = Icons;

// ── Logo URL ─────────────────────────────────────────────────────
const LOGO_URL = 'logo.png';

// ── Brand tokens ─────────────────────────────────────────────────
const B = {
  orange:  '#FF6B35',
  amber:   '#FFA500',
  white:   '#ffffff',
  gray50:  '#f9f8f7',
  gray100: '#f3f1ee',
  gray400: '#b0a99e',
  gray600: '#6b6560',
  gray800: '#2e2a26',
  gray900: '#1a1714',
  grad:    'linear-gradient(135deg, #FF6B35, #FFA500)',
  shadow:  '0 4px 20px rgba(255,107,53,0.25)',
  shadowL: '0 8px 40px rgba(255,107,53,0.18)',
};

const F = {
  display: "'Playfair Display', Georgia, serif",
  body:    "'Lora', Georgia, serif",
  mono:    "'IBM Plex Mono', 'Courier New', monospace",
};

const FONTS_STYLE = `@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&family=Lora:ital,wght@0,400;0,500;0,600;1,400;1,500&family=IBM+Plex+Mono:wght@400;500;600&display=swap');`;

// ── Error Boundary ────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, errorInfo) { console.error('Error caught by boundary:', error, errorInfo); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', background: '#f8f8f8', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div>
            <h2 style={{ color: B.orange, fontFamily: F.display }}>Algo deu errado</h2>
            <p style={{ color: B.gray600, fontFamily: F.body }}>Por favor, recarregue a página ou tente novamente mais tarde.</p>
            <button onClick={() => window.location.reload()} style={{ background: B.grad, color: 'white', border: 'none', padding: '10px 20px', borderRadius: '5px', cursor: 'pointer', marginTop: '15px' }}>
              Recarregar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── SecurityGuard ─────────────────────────────────────────────────
const SecurityGuard = () => {
  useEffect(() => {
    const noCtx = (e) => e.preventDefault();
    const noKey = (e) => {
      if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) || (e.ctrlKey && e.key === 'U')) {
        e.preventDefault();
      }
    };
    document.addEventListener('contextmenu', noCtx);
    document.addEventListener('keydown', noKey);
    return () => { document.removeEventListener('contextmenu', noCtx); document.removeEventListener('keydown', noKey); };
  }, []);
  return null;
};

// ── useInView ─────────────────────────────────────────────────────
const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold });
    observer.observe(el);
    return () => { observer.disconnect(); };
  }, [threshold]);
  return [ref, inView];
};

// ── ScrollBar ─────────────────────────────────────────────────────
const ScrollBar = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const winScroll = document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      setScrollProgress(height > 0 ? (winScroll / height) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return (
    <div style={{ position:'fixed', top:0, left:0, width:'100%', height:'2px', zIndex:9999, background:'rgba(0,0,0,0.06)' }}>
      <div style={{ height:'100%', width:`${scrollProgress}%`, background:B.grad, transition:'width 0.1s ease' }} />
    </div>
  );
};

// ── WeblyticsBadge ────────────────────────────────────────────────
const WeblyticsBadge = () => (
  <a
    href="https://weblytics.netlify.app"
    target="_blank"
    rel="noopener noreferrer"
    style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'6px 12px', borderRadius:'6px', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.1)', textDecoration:'none', transition:'all 0.3s' }}
    onMouseEnter={e => { e.currentTarget.style.background='rgba(4,0,255,0.05)'; e.currentTarget.style.borderColor='rgba(4,0,255,0.3)'; }}
    onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.03)'; e.currentTarget.style.borderColor='rgba(255,255,255,0.1)'; }}
  >
    <div style={{ width:'16px', height:'16px', borderRadius:'4px', background:'linear-gradient(135deg,rgba(4,0,255,0.15),rgba(174,0,255,0.15))', border:'1px solid rgba(4,0,255,0.3)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <circle cx="5" cy="5" r="4" stroke="url(#wg)" strokeWidth="1.5"/>
        <defs><linearGradient id="wg" x1="0" y1="0" x2="10" y2="10" gradientUnits="userSpaceOnUse"><stop stopColor="#0400ff"/><stop offset="1" stopColor="#ae00ff"/></linearGradient></defs>
      </svg>
    </div>
    <span style={{ fontSize:'11px', color:'rgba(255,255,255,0.45)', fontFamily:F.mono }}>
      Dev by <span style={{ background:'linear-gradient(90deg,#0400ff,#ae00ff)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', fontWeight:600 }}>Weblytics</span>
    </span>
  </a>
);

// ════════════════════════════════════════════════════════════════
//  MODAL
// ════════════════════════════════════════════════════════════════
const NicheModal = ({ onSelect }) => {
  const [hoveredNiche, setHoveredNiche] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => { document.body.style.overflow = ''; window.removeEventListener('resize', checkMobile); };
  }, []);

  const handleSelect = (niche) => { document.body.style.overflow = ''; onSelect(niche); };

  if (isMobile) {
    return (
      <div style={{ position:'fixed', inset:0, zIndex:9999, overflowY:'auto', background:B.white, padding:'16px' }}>
        <div style={{ maxWidth:'400px', margin:'0 auto', padding:'40px 0', display:'flex', flexDirection:'column', gap:'16px' }}>

          {/* Mobile Telecom card */}
          <button
            onClick={() => handleSelect('telecom')}
            style={{ position:'relative', background:B.gray900, borderRadius:'12px', padding:'32px 20px', border:'none', cursor:'pointer', textAlign:'center', width:'100%' }}
          >
            <div style={{ position:'absolute', inset:0, backgroundImage:`linear-gradient(rgba(255,107,53,0.04) 1px,transparent 1px)`, backgroundSize:'30px 30px', borderRadius:'12px' }} />
            <div style={{ position:'relative', zIndex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'16px' }}>
              <img src={LOGO_URL} alt="Luminê" style={{ width:'72px', height:'auto', filter:'brightness(0) invert(1)' }} />
              <div>
                <div style={{ fontSize:'9px', fontFamily:F.mono, letterSpacing:'0.2em', textTransform:'uppercase', color:'rgba(255,165,0,0.6)', marginBottom:'8px' }}>Módulo 01</div>
                <h2 style={{ fontFamily:F.display, fontWeight:700, fontSize:'22px', color:B.white, lineHeight:1.2, marginBottom:'6px' }}>Telecomunicações</h2>
                <p style={{ fontSize:'12px', color:'rgba(255,255,255,0.45)', fontFamily:F.body }}>Cadastro GIS, NT 16 e aprovação ANATEL</p>
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', justifyContent:'center', gap:'6px' }}>
                {['GIS 3D','NT 16','ANATEL'].map(t => (
                  <span key={t} style={{ fontSize:'10px', padding:'4px 10px', borderRadius:'4px', background:'rgba(255,107,53,0.12)', border:'1px solid rgba(255,107,53,0.25)', color:B.orange, fontFamily:F.mono }}>{t}</span>
                ))}
              </div>
              <span style={{ fontSize:'12px', fontFamily:F.mono, color:B.orange }}>Acessar →</span>
            </div>
          </button>

          {/* Mobile Iluminação card */}
          <button
            onClick={() => handleSelect('iluminacao')}
            style={{ position:'relative', background:B.white, borderRadius:'12px', padding:'32px 20px', border:'2px solid rgba(255,107,53,0.15)', cursor:'pointer', textAlign:'center', width:'100%' }}
          >
            <div style={{ position:'relative', zIndex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'16px' }}>
              <img src={LOGO_URL} alt="Luminê" style={{ width:'72px', height:'auto' }} />
              <div>
                <div style={{ fontSize:'9px', fontFamily:F.mono, letterSpacing:'0.15em', textTransform:'uppercase', color:'rgba(255,107,53,0.5)', marginBottom:'8px' }}>Gestão Municipal</div>
                <h2 style={{ fontFamily:F.display, fontWeight:700, fontSize:'22px', color:B.gray900, lineHeight:1.2, marginBottom:'6px' }}>Iluminação Pública</h2>
                <p style={{ fontSize:'12px', color:B.gray600, fontFamily:F.body }}>Censo, GIS e auditoria CIP</p>
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', justifyContent:'center', gap:'6px' }}>
                {['Censo','Auditoria','Fatura'].map(t => (
                  <span key={t} style={{ fontSize:'10px', padding:'4px 10px', borderRadius:'4px', background:'rgba(255,107,53,0.07)', border:'1px solid rgba(255,107,53,0.18)', color:B.orange, fontFamily:F.mono }}>{t}</span>
                ))}
              </div>
              <span style={{ fontSize:'12px', color:B.orange, fontFamily:F.mono }}>Iniciar Diagnóstico →</span>
            </div>
          </button>

        </div>
      </div>
    );
  }

  return (
    <div style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', overflow:'hidden' }}>

      {/* Desktop Telecom panel */}
      <button
        onClick={() => handleSelect('telecom')}
        onMouseEnter={() => setHoveredNiche('telecom')}
        onMouseLeave={() => setHoveredNiche(null)}
        style={{ flex: hoveredNiche==='telecom' ? 1.4 : hoveredNiche==='iluminacao' ? 0.6 : 1, position:'relative', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', border:'none', cursor:'pointer', overflow:'hidden', transition:'flex 0.6s cubic-bezier(0.4,0,0.2,1)', background:B.gray900 }}
      >
        <div style={{ position:'absolute', inset:0, backgroundImage:`linear-gradient(rgba(255,107,53,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(255,107,53,0.04) 1px,transparent 1px)`, backgroundSize:'50px 50px' }} />
        <div style={{ position:'absolute', top:'30%', left:'50%', transform:'translate(-50%,-50%)', width:'500px', height:'500px', borderRadius:'50%', background:'radial-gradient(circle,rgba(255,107,53,0.1),transparent 65%)', filter:'blur(30px)', opacity: hoveredNiche==='telecom' ? 1 : 0.4, transition:'opacity 0.5s' }} />
        <div style={{ position:'relative', zIndex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'24px', padding:'0 48px', textAlign:'center' }}>
          <img
            src={LOGO_URL}
            alt="Luminê"
            style={{ width:'150px', height:'auto', filter:'brightness(0) invert(1)', transition:'all 0.4s', transform: hoveredNiche==='telecom' ? 'scale(1.06) rotate(-3deg)' : 'scale(1)' }}
          />
          <div>
            <div style={{ fontSize:'10px', fontFamily:F.mono, letterSpacing:'0.22em', textTransform:'uppercase', color:'rgba(255,165,0,0.6)', marginBottom:'12px' }}>Módulo 01</div>
            <h2 style={{ fontFamily:F.display, fontWeight:700, fontSize:'clamp(28px,3.5vw,46px)', color:B.white, lineHeight:1.15, marginBottom:'14px' }}>Telecomunicações</h2>
            <p style={{ fontSize:'15px', color:'rgba(255,255,255,0.45)', maxWidth:'280px', lineHeight:1.7, fontFamily:F.body, fontStyle:'italic' }}>
              Cadastro GIS completo, NT 16 e aprovação ANATEL com garantia contratual
            </p>
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', justifyContent:'center', gap:'8px', maxWidth:'300px' }}>
            {['GIS 3D','NT 16','AutoCAD','ANATEL'].map(t => (
              <span key={t} style={{ fontSize:'11px', padding:'5px 13px', borderRadius:'4px', fontFamily:F.mono, background:'rgba(255,107,53,0.12)', border:`1px solid rgba(255,107,53,${hoveredNiche==='telecom' ? 0.5 : 0.25})`, color: hoveredNiche==='telecom' ? B.orange : 'rgba(255,165,0,0.6)', transition:'all 0.3s' }}>{t}</span>
            ))}
          </div>
          <div style={{ opacity: hoveredNiche==='telecom' ? 1 : 0.3, transition:'opacity 0.3s' }}>
            <span style={{ fontSize:'13px', fontFamily:F.mono, color:B.orange }}>Acessar →</span>
          </div>
        </div>
        <div style={{ position:'absolute', bottom:0, left:0, right:0, height:'2px', background: hoveredNiche==='telecom' ? B.grad : 'transparent', transition:'all 0.4s' }} />
      </button>

      <div style={{ width:'1px', flexShrink:0, background:'rgba(255,255,255,0.08)', position:'relative', zIndex:10 }}>
        <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:'36px', height:'36px', borderRadius:'50%', background:B.white, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 4px 20px rgba(0,0,0,0.15)' }}>
          <span style={{ fontSize:'11px', fontWeight:600, color:B.gray600, fontFamily:F.body }}>ou</span>
        </div>
      </div>

      {/* Desktop Iluminação panel */}
      <button
        onClick={() => handleSelect('iluminacao')}
        onMouseEnter={() => setHoveredNiche('iluminacao')}
        onMouseLeave={() => setHoveredNiche(null)}
        style={{ flex: hoveredNiche==='iluminacao' ? 1.4 : hoveredNiche==='telecom' ? 0.6 : 1, position:'relative', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', border:'none', cursor:'pointer', overflow:'hidden', transition:'flex 0.6s cubic-bezier(0.4,0,0.2,1)', background:B.white }}
      >
        <div style={{ position:'absolute', inset:0, backgroundImage:`radial-gradient(circle,rgba(255,107,53,0.06) 1.5px,transparent 1.5px)`, backgroundSize:'28px 28px' }} />
        <div style={{ position:'absolute', top:'30%', left:'50%', transform:'translate(-50%,-50%)', width:'500px', height:'500px', borderRadius:'50%', background:'radial-gradient(circle,rgba(255,165,0,0.08),transparent 65%)', filter:'blur(30px)', opacity: hoveredNiche==='iluminacao' ? 1 : 0.4, transition:'opacity 0.5s' }} />
        <div style={{ position:'relative', zIndex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'24px', padding:'0 48px', textAlign:'center' }}>
          <img
            src={LOGO_URL}
            alt="Luminê"
            style={{ width:'150px', height:'auto', transition:'all 0.4s', transform: hoveredNiche==='iluminacao' ? 'scale(1.06)' : 'scale(1)' }}
          />
          <div>
            <div style={{ fontSize:'10px', fontFamily:F.mono, letterSpacing:'0.14em', textTransform:'uppercase', color:'rgba(255,107,53,0.5)', marginBottom:'12px' }}>Gestão Municipal</div>
            <h2 style={{ fontFamily:F.display, fontWeight:700, fontSize:'clamp(28px,3.5vw,46px)', color:B.gray900, lineHeight:1.15, marginBottom:'14px' }}>Iluminação Pública</h2>
            <p style={{ fontSize:'15px', color:B.gray600, maxWidth:'280px', lineHeight:1.7, fontFamily:F.body, fontStyle:'italic' }}>
              Censo, GIS, auditoria de fatura CIP e tratativas com distribuidora
            </p>
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', justifyContent:'center', gap:'8px', maxWidth:'300px' }}>
            {['Censo Digital','Auditoria CIP','Fatura','Tratativas'].map(t => (
              <span key={t} style={{ fontSize:'11px', padding:'5px 13px', borderRadius:'4px', fontFamily:F.mono, background:'rgba(255,107,53,0.07)', border:`1px solid rgba(255,107,53,${hoveredNiche==='iluminacao' ? 0.4 : 0.18})`, color: hoveredNiche==='iluminacao' ? B.orange : B.gray600, transition:'all 0.3s' }}>{t}</span>
            ))}
          </div>
          <div style={{ opacity: hoveredNiche==='iluminacao' ? 1 : 0.3, transition:'opacity 0.3s' }}>
            <span style={{ fontSize:'13px', fontFamily:F.mono, color:B.orange }}>Iniciar Diagnóstico →</span>
          </div>
        </div>
        <div style={{ position:'absolute', bottom:0, left:0, right:0, height:'2px', background: hoveredNiche==='iluminacao' ? B.grad : 'transparent', transition:'all 0.4s' }} />
      </button>

    </div>
  );
};

// ════════════════════════════════════════════════════════════════
//  TELECOM SITE
// ════════════════════════════════════════════════════════════════
const TC_WA_TEXT  = 'Olá! Preciso de informações sobre regularização de infraestrutura com a Luminê.';
const TC_WA_JOAO  = `https://wa.me/5591999830601?text=${encodeURIComponent(TC_WA_TEXT)}`;
const TC_WA_TIAGO = `https://wa.me/5591992085291?text=${encodeURIComponent(TC_WA_TEXT)}`;

const TelecomSite = ({ onSwitch }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [termLines, setTermLines] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatStep, setChatStep] = useState('menu');
  const [chatMsgs, setChatMsgs] = useState([{ from:'bot', text:'Sistema Luminê disponível. Como podemos auxiliá-lo?' }]);
  const [isMobile, setIsMobile] = useState(false);
  const msgEnd = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const LINES = [
      '> Iniciando cadastro GIS...',
      '> Validando NT 16... [OK]',
      '> Sincronizando ANATEL... [CONECTADO]',
      '> Processando 75.000 pontos...',
      '> Exportando AutoCAD... [CONCLUÍDO]',
      '> Aprovação garantida ✓'
    ];
    let index = 0;
    let isActive = true;
    const safe = (updater) => { if (isActive) setTermLines(updater); };
    safe([LINES[0]]);
    index = 1;
    const intervalId = setInterval(() => {
      if (!isActive) return;
      if (index < LINES.length) {
        safe(prev => Array.isArray(prev) ? [...prev, LINES[index]] : [LINES[index]]);
        index++;
      } else {
        safe([LINES[0]]);
        index = 1;
      }
    }, 1800);
    return () => { isActive = false; clearInterval(intervalId); };
  }, []);

  useEffect(() => {
    if (msgEnd.current) msgEnd.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMsgs]);

  const scrollTo = useCallback((id) => {
    setMenuOpen(false);
    const attemptScroll = (attempt = 0) => {
      const element = document.getElementById(id);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        return true;
      }
      return false;
    };
    if (attemptScroll()) return;
    let attempts = 0;
    const interval = setInterval(() => {
      if (attemptScroll()) { clearInterval(interval); }
      else if (attempts >= 10) { clearInterval(interval); }
      attempts++;
    }, 200);
    setTimeout(() => clearInterval(interval), 3000);
  }, []);

  const addMsg = useCallback((from, text, delay = 0) => {
    setTimeout(() => setChatMsgs(prev => [...prev, { from, text }]), delay);
  }, []);

  const chatAct = useCallback((key) => {
    if (key === 'prazo') {
      addMsg('user', 'Qual o prazo de execução?');
      addMsg('bot', 'Entre 3 e 5 meses. Modalidade Express: 30 dias.', 500);
      setChatStep('fu');
    } else if (key === 'anatel') {
      addMsg('user', 'Como funciona a aprovação ANATEL?');
      addMsg('bot', 'Aprovação 100% garantida em contrato. Dossiê completo conforme NT 16.', 500);
      setChatStep('fu');
    } else if (key === 'orcamento') {
      addMsg('user', 'Como solicitar orçamento?');
      addMsg('bot', 'Orçamento sem custo. Retorno em até 2 horas úteis.', 500);
      setChatStep('fu');
    } else if (key === 'wa') {
      addMsg('user', 'Falar com especialista');
      addMsg('bot', 'Conectando ao time técnico...', 500);
      setTimeout(() => window.open(TC_WA_JOAO, '_blank'), 1200);
    } else if (key === 'back') {
      addMsg('user', 'Voltar');
      addMsg('bot', 'Como podemos ajudar?', 400);
      setChatStep('menu');
    }
  }, [addMsg]);

  const services = useMemo(() => [
    { icon: MapPinned,  title: 'Cadastro GIS 3D',          desc: 'BIM, LiDAR e drone — precisão submilimétrica para regularização completa',                                                                         tags: ['BIM 360','LiDAR','RTK'] },
    { icon: FileText,   title: 'Projetos AutoCAD',          desc: 'Desenhos 2D/3D executivos com ART e responsabilidade técnica inclusa',                                                                             tags: ['CAD 2024','Executivos','ART'] },
    { icon: Shield,     title: 'Regularização NT 16',       desc: 'Dossiê técnico completo para submissão e aprovação junto à ANATEL',                                                                                tags: ['NT 16','Dossiê','Tracking'] },
    { icon: FileText,   title: 'Memorial Descritivo',       desc: 'Documento técnico completo informando toda a parte técnica e operacional do processo, seja ele incremento ou remoção',                             tags: ['Documentação','ART','Operacional'] },
    { icon: Database,   title: 'Planilha Sequencial',       desc: 'Informações detalhadas referentes aos postes utilizados ou removidos',                                                                             tags: ['Postes','Inventário','Controle'] },
    { icon: Cpu,        title: 'Cálculo de Esforço',        desc: 'Análise estrutural dos esforços exercidos pela fibra e pelo poste',                                                                                tags: ['Estrutural','Laudos','Segurança'] },
    { icon: Globe2,     title: 'KMZ / KML',                 desc: 'Arquivos georreferenciados com quantitativos da extensão da fibra e quantidade de postes',                                                         tags: ['KMZ','KML','Geoespacial'] },
    { icon: Headphones, title: 'Suporte Dedicado',          desc: 'Engenheiro responsável alocado por projeto durante toda a execução',                                                                               tags: ['24/7','Eng. Resp.','WhatsApp'] },
    { icon: GitMerge,   title: 'Permuta de Pontos Parcial', desc: 'Executamos projetos de permuta de pontos parcial, garantindo ao provedor a permuta dos pontos 100% aprovada.',                                     tags: ['Permuta','100% Aprovada','ANATEL'] },
    { icon: ScanLine,   title: 'Remoção de Pontos',         desc: 'Projetos onde o provedor irá pagar somente o que estiver utilizando, caso tenha pontos ociosos ou que foram cadastrados indevidamente.',           tags: ['Pontos Ociosos','Regularização','Economia'] },
    { icon: HelpCircle, title: 'Consultoria Especializada', desc: 'Orientação técnica e estratégica para provedores em todas as etapas do processo de regularização junto à ANATEL e distribuidoras.',               tags: ['Técnica','Estratégica','ANATEL'] },
    { icon: HelpCircle, title: 'Autodeclaração',            desc: 'Declaração de pontos junto a concessionária, para regularização sem envio de projetos. (Restrito a região do Rio Grande do Sul por 90 dias).',    tags: ['Regularização','Economia'] },
  ], []);

  const process = useMemo(() => [
    { n:'01', title:'Diagnóstico Regulatório', desc:'Análise completa da situação cadastral e regulatória',        time:'3–5 dias',    icon:FileSearch },
    { n:'02', title:'Levantamento GIS',         desc:'Campo com drone e LiDAR para mapeamento submilimétrico',       time:'2–4 semanas', icon:MapPinned  },
    { n:'03', title:'Memorial Descritivo',      desc:'Elaboração do documento técnico com toda a parte operacional', time:'1–2 semanas', icon:FileText   },
    { n:'04', title:'Planilha Sequencial',      desc:'Organização das informações de postes e estrutura',            time:'1–2 semanas', icon:Database   },
    { n:'05', title:'Cálculo de Esforço',       desc:'Análise estrutural dos esforços da fibra e poste',             time:'1 semana',    icon:Cpu        },
    { n:'06', title:'Arquivos KMZ/KML',         desc:'Geração de arquivos georreferenciados com quantitativos',      time:'1 semana',    icon:Globe2     },
  ], []);

  const cases = useMemo(() => [
    { client:'Nevoli',     tag:'Regularização de projetos', result:'100% Aprovação', desc:'Regularização de pontos projetados com 100% de aprovação junto à ANATEL em tempo recorde.',                                                                 metrics:[['100%','Aprovação'],['25 dias','Prazo'],['Projetos','Regularizados']] },
    { client:'Nippontec',  tag:'Inserção de pontos no GEOS', result:'100% Aprovação', desc:'Cadastro de pontos e ajustes junto à concessionária, garantindo conformidade total com o sistema GEOS.',                                                     metrics:[['100%','Aprovação'],['18 dias','Prazo'],['GEOS','Integrado']] },
    { client:'Prymus',     tag:'Remoção de pontos indevidos', result:'100% Aprovação', desc:'Remoção de pontos que o provedor não utilizava efetivamente, resultando em uma fatura mais justa e redução de custos operacionais.',                        metrics:[['100%','Aprovação'],['26 dias','Prazo'],['Remoção','Pontos Indevidos']] },
  ], []);

  const faqs = useMemo(() => [
    { q:'O que é a NT 16?',                     a:'Norma técnica da ANATEL que define os requisitos para regularização de estruturas de suporte de antenas. Sua conformidade é obrigatória para operação legal no território nacional.' },
    { q:'A aprovação é garantida em contrato?',  a:'Sim. Garantia integral formalizada em contrato. Em caso de rejeição, reexecutamos o processo sem custo adicional até a aprovação.' },
    { q:'Qual o prazo para aprovação completa?', a:'Entre 3 e 5 meses em média. Para a modalidade Express, projetos elegíveis são aprovados em até 30 dias.' },
    { q:'Como funciona o cadastro GIS?',         a:'Utilizamos drones com LiDAR, GPS RTK e BIM 360 para mapeamento submilimétrico, integrado diretamente ao sistema ANATEL.' },
    { q:'Atendem em todo o Brasil?',             a:'Sim. Atuamos em todo o território nacional com equipes especializadas.' },
  ], []);

  const TServiceCard = useCallback(({ s, i }) => {
    const Icon = s.icon;
    const [ref, inView] = useInView();
    const [hov, setHov] = useState(false);
    return (
      <div ref={ref} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        style={{ background: hov ? 'rgba(255,107,53,0.04)' : B.gray900, padding: isMobile ? '20px' : '28px', transition:'background 0.25s', opacity: inView ? 1 : 0, animationName: inView ? 'tcfade' : 'none', animationDuration:'0.5s', animationTimingFunction:'ease-out', animationFillMode:'both', animationDelay:`${i*55}ms` }}
      >
        <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'14px' }}>
          <div style={{ width:'36px', height:'36px', borderRadius:'6px', background: hov ? 'rgba(255,107,53,0.18)' : 'rgba(255,107,53,0.07)', border:`1px solid rgba(255,107,53,${hov ? 0.45 : 0.18})`, display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.25s' }}>
            <Icon style={{ width:'16px', height:'16px', color:B.orange }} />
          </div>
          <div>
            <div style={{ fontFamily:F.mono, fontSize:'9px', color:'rgba(255,165,0,0.3)', letterSpacing:'0.14em', marginBottom:'2px', textTransform:'uppercase' }}>MOD_{String(i+1).padStart(2,'0')}</div>
            <h3 style={{ fontFamily:F.display, fontWeight:600, fontSize:'15px', color: hov ? B.orange : B.white, transition:'color 0.25s' }}>{s.title}</h3>
          </div>
        </div>
        <p style={{ fontSize:'13px', color:'rgba(255,255,255,0.36)', lineHeight:1.7, marginBottom:'14px', fontFamily:F.body, fontStyle:'italic' }}>{s.desc}</p>
        <div style={{ display:'flex', gap:'5px', flexWrap:'wrap' }}>
          {s.tags.map(t => (<span key={t} style={{ fontFamily:F.mono, fontSize:'9px', padding:'3px 8px', borderRadius:'2px', background:'rgba(255,107,53,0.06)', border:'1px solid rgba(255,107,53,0.12)', color:'rgba(255,165,0,0.45)' }}>{t}</span>))}
        </div>
      </div>
    );
  }, [isMobile]);

  const TProcessRow = useCallback(({ st, i }) => {
    const Icon = st.icon;
    const [ref, inView] = useInView();
    return (
      <div ref={ref} style={{ display:'flex', flexDirection: isMobile ? 'column' : 'row', borderBottom:'1px solid rgba(255,107,53,0.06)', opacity: inView ? 1 : 0, transition:'all 0.5s', transitionDelay:`${i*70}ms`, transform: inView ? 'none' : 'translateX(-16px)' }}>
        <div style={{ width: isMobile ? '100%' : '80px', flexShrink:0, padding: isMobile ? '16px' : '28px 24px', borderRight: isMobile ? 'none' : '1px solid rgba(255,107,53,0.08)', borderBottom: isMobile ? '1px solid rgba(255,107,53,0.08)' : 'none', display:'flex', flexDirection:'row', alignItems:'center', justifyContent: isMobile ? 'flex-start' : 'center', gap:'8px' }}>
          <div style={{ width:'28px', height:'28px', borderRadius:'50%', background:'rgba(255,107,53,0.1)', border:'1px solid rgba(255,107,53,0.3)', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <Icon style={{ width:'12px', height:'12px', color:B.orange }} />
          </div>
          <span style={{ fontFamily:F.mono, fontSize:'10px', color:'rgba(255,165,0,0.3)', letterSpacing:'0.1em' }}>{st.n}</span>
        </div>
        <div style={{ flex:1, padding: isMobile ? '16px' : '28px 32px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'6px', flexWrap:'wrap' }}>
            <h3 style={{ fontFamily:F.display, fontWeight:600, fontSize:'17px', color:B.white }}>{st.title}</h3>
            <span style={{ marginLeft: isMobile ? '0' : 'auto', fontFamily:F.mono, fontSize:'10px', color:B.orange, background:'rgba(255,107,53,0.07)', border:'1px solid rgba(255,107,53,0.18)', padding:'3px 10px', borderRadius:'2px' }}>{st.time}</span>
          </div>
          <p style={{ fontSize:'13px', color:'rgba(255,255,255,0.36)', lineHeight:1.7, fontFamily:F.body, fontStyle:'italic' }}>{st.desc}</p>
        </div>
      </div>
    );
  }, [isMobile]);

  const TCaseCard = useCallback(({ c, i }) => {
    const [ref, inView] = useInView();
    return (
      <div ref={ref} style={{ background:B.gray900, padding: isMobile ? '24px' : '36px', opacity: inView ? 1 : 0, transition:'all 0.5s', transitionDelay:`${i*90}ms`, transform: inView ? 'none' : 'translateY(16px)' }}>
        <div style={{ fontFamily:F.mono, fontSize:'9px', color:'rgba(255,165,0,0.4)', letterSpacing:'0.18em', textTransform:'uppercase', marginBottom:'8px' }}>{c.tag}</div>
        <h3 style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? '18px' : '20px', color:B.white, marginBottom:'6px' }}>{c.client}</h3>
        <div style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? '26px' : '30px', color:B.orange, letterSpacing:'-0.01em', marginBottom:'12px' }}>{c.result}</div>
        <p style={{ fontSize:'13px', color:'rgba(255,255,255,0.38)', lineHeight:1.7, marginBottom:'20px', fontFamily:F.body, fontStyle:'italic' }}>{c.desc}</p>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'1px', background:'rgba(255,107,53,0.1)' }}>
          {c.metrics.map(([v,l],j) => (
            <div key={j} style={{ background:B.gray900, padding:'10px', textAlign:'center' }}>
              <div style={{ fontFamily:F.mono, fontSize:'13px', fontWeight:500, color:B.amber }}>{v}</div>
              <div style={{ fontFamily:F.mono, fontSize:'9px', color:'rgba(255,255,255,0.25)', marginTop:'2px' }}>{l}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }, [isMobile]);

  const TFaqItem = useCallback(({ it }) => {
    const [open, setOpen] = useState(false);
    return (
      <div style={{ background:B.gray900 }}>
        <button onClick={() => setOpen(!open)} style={{ width:'100%', padding: isMobile ? '16px' : '20px 24px', display:'flex', justifyContent:'space-between', alignItems:'center', background:'none', border:'none', cursor:'pointer', textAlign:'left' }}>
          <span style={{ fontFamily:F.display, fontSize: isMobile ? '13px' : '14px', fontWeight:600, color: open ? B.orange : 'rgba(255,255,255,0.65)', paddingRight:'16px' }}>{it.q}</span>
          <ChevronDown style={{ color:B.orange, width:'15px', height:'15px', flexShrink:0, transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.3s' }} />
        </button>
        {open && (
          <div style={{ padding: isMobile ? '0 16px 16px' : '0 24px 20px', fontFamily:F.body, fontSize:'13px', color:'rgba(255,255,255,0.38)', lineHeight:1.8, fontStyle:'italic' }}>{it.a}</div>
        )}
      </div>
    );
  }, [isMobile]);

  return (
    <div style={{ background:B.gray900, color:B.white, fontFamily:F.body, minHeight:'100vh' }}>
      <style>{`
        @keyframes tcblink{0%,100%{opacity:1}50%{opacity:0.3}}
        @keyframes tcblink2{0%,49%{opacity:1}50%,100%{opacity:0}}
        @keyframes tcfade{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
        ${FONTS_STYLE}
      `}</style>
      <SecurityGuard />
      <ScrollBar />

      {/* ── HEADER ─────────────────────────────────────────────── */}
      <header style={{ position:'fixed', top:'2px', left:0, right:0, zIndex:100, height:'68px', background: scrolled ? 'rgba(26,23,20,0.97)' : 'rgba(26,23,20,0.82)', backdropFilter:'blur(20px)', borderBottom:`1px solid rgba(255,107,53,${scrolled ? 0.18 : 0.07})`, transition:'all 0.3s', display:'flex', alignItems:'center' }}>
        <div style={{ maxWidth:'1280px', margin:'0 auto', padding:'0 16px', width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between' }}>

          {/* Logo */}
          <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
            <img
              src={LOGO_URL}
              alt="Luminê"
              style={{ height: isMobile ? '90px' : '90px', width:'auto', filter:'brightness(0) invert(1)' }}
            />
          </div>

          <nav style={{ display:'flex', alignItems:'center', gap: isMobile ? '8px' : '28px' }}>
            {!isMobile && ['servicos','processo','cases','faq'].map(id => (
              <button key={id} onClick={() => scrollTo(id)}
                style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(255,255,255,0.45)', fontSize:'13px', fontFamily:F.body, transition:'color 0.2s', padding:'4px 0' }}
                onMouseEnter={e => e.target.style.color = B.orange}
                onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.45)'}
              >
                {id.charAt(0).toUpperCase()+id.slice(1)}
              </button>
            ))}
            <button onClick={onSwitch}
              style={{ background:'none', border:`1px solid rgba(255,107,53,0.25)`, borderRadius:'4px', padding: isMobile ? '4px 8px' : '5px 12px', cursor:'pointer', color:'rgba(255,165,0,0.55)', fontSize: isMobile ? '11px' : '12px', fontFamily:F.mono, transition:'all 0.2s' }}
              onMouseEnter={e => { e.target.style.background='rgba(255,107,53,0.08)'; e.target.style.color=B.orange; }}
              onMouseLeave={e => { e.target.style.background='none'; e.target.style.color='rgba(255,165,0,0.55)'; }}
            >
              {isMobile ? '⇄' : 'Iluminação'}
            </button>
            {!isMobile && (
              <a href={TC_WA_JOAO} target="_blank" rel="noopener noreferrer"
                style={{ display:'flex', alignItems:'center', gap:'7px', padding:'9px 18px', borderRadius:'6px', background:B.grad, color:B.white, textDecoration:'none', fontSize:'13px', fontWeight:600, fontFamily:F.body, boxShadow:B.shadow }}
              >
                <Phone style={{ width:'13px', height:'13px' }}/> Contato
              </a>
            )}
          </nav>

          {isMobile && (
            <button onClick={() => setMenuOpen(!menuOpen)} style={{ background:'none', border:`1px solid rgba(255,107,53,0.28)`, borderRadius:'6px', padding:'7px', cursor:'pointer', display:'flex' }}>
              <Menu style={{ color:B.orange, width:'18px', height:'18px' }}/>
            </button>
          )}
        </div>

        {menuOpen && (
          <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'rgba(26,23,20,0.99)', borderBottom:`1px solid rgba(255,107,53,0.12)`, padding:'16px', display:'flex', flexDirection:'column', gap:'4px' }}>
            {['servicos','processo','cases','faq'].map(id => (
              <button key={id} onClick={() => scrollTo(id)} style={{ background:'none', border:'none', cursor:'pointer', textAlign:'left', color:'rgba(255,255,255,0.6)', padding:'10px 0', fontFamily:F.body, fontSize:'14px', borderBottom:'1px solid rgba(255,107,53,0.07)' }}>
                {id.charAt(0).toUpperCase()+id.slice(1)}
              </button>
            ))}
            <button onClick={onSwitch} style={{ background:'none', border:'none', cursor:'pointer', textAlign:'left', color:B.orange, padding:'10px 0', fontFamily:F.body, fontSize:'13px' }}>
              Mudar para Iluminação Pública
            </button>
            <a href={TC_WA_JOAO} target="_blank" rel="noopener noreferrer" style={{ marginTop:'8px', textAlign:'center', padding:'12px', background:B.grad, borderRadius:'6px', color:B.white, textDecoration:'none', fontWeight:600, fontFamily:F.body }}>
              Falar com Especialista
            </a>
          </div>
        )}
      </header>

      {/* ── HERO ───────────────────────────────────────────────── */}
      <section style={{ position:'relative', minHeight:'100vh', display:'flex', alignItems:'center', paddingTop:'68px', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:`linear-gradient(rgba(255,107,53,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,107,53,0.03) 1px,transparent 1px)`, backgroundSize:'60px 60px' }} />
        <div style={{ position:'absolute', top:'25%', right:'-5%', width: isMobile ? '300px' : '550px', height: isMobile ? '300px' : '550px', borderRadius:'50%', background:'radial-gradient(circle,rgba(255,107,53,0.06),transparent 70%)', filter:'blur(50px)' }} />
        <div style={{ maxWidth:'1280px', margin:'0 auto', padding:'0 16px', width:'100%', display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '32px' : '64px', alignItems:'center' }}>
          <div>
            <div style={{ display:'inline-flex', alignItems:'center', gap:'8px', padding:'5px 14px', borderRadius:'3px', background:'rgba(255,107,53,0.1)', border:'1px solid rgba(255,107,53,0.28)', marginBottom:'24px', fontFamily:F.mono, fontSize:'10px', color:B.orange, letterSpacing:'0.16em' }}>
              <span style={{ width:'7px', height:'7px', borderRadius:'50%', background:B.orange, animation:'tcblink 2s infinite' }} />
              {isMobile ? 'NT 16 Certificado' : 'Sistema Ativo — NT 16 Certificado'}
            </div>
            <h1 style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? 'clamp(34px,9vw,46px)' : 'clamp(42px,5.5vw,80px)', lineHeight:1.05, letterSpacing:'-0.02em', marginBottom:'24px' }}>
              <span style={{ display:'block' }}>Regularize</span>
              <span style={{ display:'block', color:B.orange }}>sua</span>
              <span style={{ display:'block' }}>Infraestrutura.</span>
            </h1>
            <p style={{ fontSize: isMobile ? '15px' : '17px', color:'rgba(255,255,255,0.5)', lineHeight:1.8, marginBottom:'36px', maxWidth:'480px', fontFamily:F.body, fontStyle:'italic' }}>
              Cadastro GIS completo, projetos técnicos e aprovação garantida. Conformidade total com NT 16 e ANATEL. Atuamos em todo o Brasil.
            </p>
          </div>
          {!isMobile && (
            <div>
              <div style={{ background:'rgba(0,0,0,0.45)', border:'1px solid rgba(255,107,53,0.18)', borderRadius:'8px', overflow:'hidden' }}>
                <div style={{ background:'rgba(255,107,53,0.07)', padding:'10px 16px', display:'flex', alignItems:'center', gap:'8px', borderBottom:'1px solid rgba(255,107,53,0.1)' }}>
                  <span style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#ff5f57' }}/>
                  <span style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#ffbd2e' }}/>
                  <span style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#28ca41' }}/>
                  <span style={{ marginLeft:'8px', fontFamily:F.mono, fontSize:'11px', color:'rgba(255,165,0,0.45)' }}>lumine_telecom.sh</span>
                </div>
                <div style={{ padding:'20px', minHeight:'200px' }}>
                  {Array.isArray(termLines) && termLines.length > 0 ? (
                    termLines.map((line, i) => {
                      if (!line || typeof line !== 'string') return null;
                      let color = 'rgba(255,255,255,0.45)';
                      if (line.includes('[OK]') || line.includes('[CONCLUÍDO]') || line.includes('✓')) color = '#28ca41';
                      else if (line.includes('[CONECTADO]')) color = B.orange;
                      return <div key={i} style={{ fontFamily:F.mono, fontSize:'12px', marginBottom:'8px', color }}>{line}</div>;
                    })
                  ) : (
                    <div style={{ fontFamily:F.mono, fontSize:'12px', color:'rgba(255,255,255,0.45)' }}>Aguardando...</div>
                  )}
                  <div style={{ fontFamily:F.mono, fontSize:'12px', color:B.orange, animation:'tcblink2 1s infinite' }}>▊</div>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'1px', marginTop:'16px', background:'rgba(255,107,53,0.12)' }}>
                {[['75.000+','Pontos GIS'],['30 dias','Express'],['4 anos','Experiência'],['Todo o','Brasil']].map(([v,l],i) => (
                  <div key={i} style={{ background:B.gray900, padding:'16px 12px', textAlign:'center' }}>
                    <div style={{ fontFamily:F.display, fontWeight:700, fontSize:'20px', color:B.orange }}>{v}</div>
                    <div style={{ fontFamily:F.mono, fontSize:'9px', color:'rgba(255,255,255,0.3)', marginTop:'4px', letterSpacing:'0.1em', textTransform:'uppercase' }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── SERVIÇOS ───────────────────────────────────────────── */}
      <section id="servicos" style={{ padding: isMobile ? '60px 0' : '80px 0', borderTop:`1px solid rgba(255,107,53,0.08)` }}>
        <div style={{ maxWidth:'1280px', margin:'0 auto', padding:'0 16px' }}>
          <div style={{ marginBottom:'48px' }}>
            <div style={{ fontFamily:F.mono, fontSize:'10px', color:B.orange, letterSpacing:'0.28em', textTransform:'uppercase', marginBottom:'12px' }}>Módulos do Sistema</div>
            <h2 style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? 'clamp(26px,7vw,34px)' : 'clamp(30px,3.5vw,50px)', letterSpacing:'-0.01em', lineHeight:1.1 }}>
              Portfólio de <span style={{ color:B.orange }}>Serviços</span>
            </h2>
          </div>
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(270px,1fr))', gap:'1px', background:'rgba(255,107,53,0.08)' }}>
            {services.map((s,i) => <TServiceCard key={s.title} s={s} i={i} />)}
          </div>
        </div>
      </section>

      {/* ── PROCESSO ───────────────────────────────────────────── */}
      <section id="processo" style={{ padding: isMobile ? '60px 0' : '80px 0', background:'rgba(255,107,53,0.02)', borderTop:`1px solid rgba(255,107,53,0.08)` }}>
        <div style={{ maxWidth:'1280px', margin:'0 auto', padding:'0 16px' }}>
          <div style={{ marginBottom:'40px' }}>
            <div style={{ fontFamily:F.mono, fontSize:'10px', color:B.orange, letterSpacing:'0.28em', textTransform:'uppercase', marginBottom:'12px' }}>Metodologia de Execução</div>
            <h2 style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? 'clamp(26px,7vw,34px)' : 'clamp(30px,3.5vw,50px)', letterSpacing:'-0.01em', lineHeight:1.1 }}>
              Como <span style={{ color:B.orange }}>Trabalhamos</span>
            </h2>
          </div>
          <div style={{ display:'flex', flexDirection:'column' }}>
            {process.map((st,i) => <TProcessRow key={st.n} st={st} i={i} />)}
          </div>
        </div>
      </section>

      {/* ── ÁREA DE ATUAÇÃO ────────────────────────────────────── */}
      <section style={{ padding: isMobile ? '60px 0' : '80px 0', background:'rgba(255,107,53,0.02)', borderTop:`1px solid rgba(255,107,53,0.08)` }}>
        <div style={{ maxWidth:'1280px', margin:'0 auto', padding:'0 16px' }}>
          <div style={{ marginBottom:'40px', textAlign:'center' }}>
            <div style={{ fontFamily:F.mono, fontSize:'10px', color:B.orange, letterSpacing:'0.28em', textTransform:'uppercase', marginBottom:'12px' }}>Presença Nacional</div>
            <h2 style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? 'clamp(26px,7vw,34px)' : 'clamp(30px,3.5vw,50px)', letterSpacing:'-0.01em', lineHeight:1.1 }}>
              Atuamos em <span style={{ color:B.orange }}>Todo o Brasil</span>
            </h2>
          </div>
          <div style={{ background:B.gray900, borderRadius:'24px', padding:'48px 32px', textAlign:'center', border:'1px solid rgba(255,107,53,0.2)' }}>
            <Globe2 style={{ width:'64px', height:'64px', color:B.orange, margin:'0 auto 20px', display:'block' }} />
            <h3 style={{ fontFamily:F.display, fontWeight:600, fontSize:'24px', color:B.white, marginBottom:'16px' }}>Cobertura Nacional</h3>
            <p style={{ fontSize:'16px', color:'rgba(255,255,255,0.6)', lineHeight:1.7, fontFamily:F.body, fontStyle:'italic', maxWidth:'600px', margin:'0 auto' }}>
              Atendemos provedores e operadoras em todo o território nacional, com equipes especializadas para atender qualquer região do Brasil.
            </p>
          </div>
          <p style={{ textAlign:'center', marginTop:'30px', color:'rgba(255,255,255,0.45)', fontSize:'14px', fontFamily:F.body, fontStyle:'italic' }}>
            Em conjunto com a Distribuidora, ANATEL e ANEEL
          </p>
        </div>
      </section>

      {/* ── CASES ──────────────────────────────────────────────── */}
      <section id="cases" style={{ padding: isMobile ? '60px 0' : '80px 0', borderTop:`1px solid rgba(255,107,53,0.08)` }}>
        <div style={{ maxWidth:'1280px', margin:'0 auto', padding:'0 16px' }}>
          <div style={{ marginBottom:'40px' }}>
            <div style={{ fontFamily:F.mono, fontSize:'10px', color:B.orange, letterSpacing:'0.28em', textTransform:'uppercase', marginBottom:'12px' }}>Projetos Concluídos</div>
            <h2 style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? 'clamp(26px,7vw,34px)' : 'clamp(30px,3.5vw,50px)', letterSpacing:'-0.01em', lineHeight:1.1 }}>
              Cases de <span style={{ color:B.orange }}>Referência</span>
            </h2>
          </div>
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(320px,1fr))', gap:'1px', background:'rgba(255,107,53,0.08)' }}>
            {cases.map((c,i) => <TCaseCard key={c.client} c={c} i={i} />)}
          </div>
        </div>
      </section>

      {/* ── FAQ ────────────────────────────────────────────────── */}
      <section id="faq" style={{ padding: isMobile ? '60px 0' : '80px 0', background:'rgba(255,107,53,0.02)', borderTop:`1px solid rgba(255,107,53,0.08)` }}>
        <div style={{ maxWidth: isMobile ? '100%' : '900px', margin:'0 auto', padding:'0 16px' }}>
          <div style={{ marginBottom:'40px', textAlign:'center' }}>
            <div style={{ fontFamily:F.mono, fontSize:'10px', color:B.orange, letterSpacing:'0.28em', textTransform:'uppercase', marginBottom:'12px' }}>Dúvidas Frequentes</div>
            <h2 style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? 'clamp(26px,7vw,34px)' : 'clamp(30px,3.5vw,50px)', letterSpacing:'-0.01em', lineHeight:1.1 }}>Perguntas Frequentes</h2>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'1px', background:'rgba(255,107,53,0.08)' }}>
            {faqs.map((it,i) => <TFaqItem key={i} it={it} />)}
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ──────────────────────────────────────────── */}
      <section style={{ padding: isMobile ? '60px 0' : '80px 0', textAlign:'center', position:'relative', overflow:'hidden', borderTop:`1px solid rgba(255,107,53,0.08)` }}>
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(135deg,rgba(255,107,53,0.03),rgba(255,165,0,0.05))' }} />
        <div style={{ position:'relative', zIndex:1, maxWidth:'640px', margin:'0 auto', padding:'0 16px' }}>
          <div style={{ fontFamily:F.mono, fontSize:'10px', color:B.orange, letterSpacing:'0.28em', textTransform:'uppercase', marginBottom:'20px' }}>Iniciar Projeto</div>
          <h2 style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? 'clamp(28px,7vw,38px)' : 'clamp(34px,4.5vw,62px)', letterSpacing:'-0.02em', lineHeight:1.05, marginBottom:'20px' }}>
            Pronto para<br/><span style={{ color:B.orange }}>Regularizar?</span>
          </h2>
          <p style={{ color:'rgba(255,255,255,0.4)', fontSize: isMobile ? '15px' : '16px', marginBottom:'32px', fontFamily:F.body, fontStyle:'italic' }}>
            Atendemos provedores em todo o território nacional.<br/>Diagnóstico gratuito. Retorno em até 2 horas úteis.
          </p>
          <div style={{ display:'flex', gap:'12px', justifyContent:'center', flexWrap:'wrap' }}>
            <a href={TC_WA_JOAO} target="_blank" rel="noopener noreferrer"
              style={{ display:'inline-flex', alignItems:'center', gap:'10px', padding: isMobile ? '13px 20px' : '16px 30px', borderRadius:'8px', textDecoration:'none', background:B.grad, color:B.white, fontWeight:600, fontSize: isMobile ? '13px' : '15px', fontFamily:F.body, boxShadow:B.shadowL }}
            >
              <Phone style={{ width:'15px', height:'15px' }}/> João — (91) 99983-0601
            </a>
            <a href={TC_WA_TIAGO} target="_blank" rel="noopener noreferrer"
              style={{ display:'inline-flex', alignItems:'center', gap:'10px', padding: isMobile ? '13px 20px' : '16px 30px', borderRadius:'8px', textDecoration:'none', background:'transparent', border:'1px solid rgba(255,107,53,0.45)', color:B.orange, fontWeight:600, fontSize: isMobile ? '13px' : '15px', fontFamily:F.body }}
            >
              <Phone style={{ width:'15px', height:'15px' }}/> Tiago — (91) 99208-5291
            </a>
          </div>
        </div>
      </section>

      {/* ── FOOTER ─────────────────────────────────────────────── */}
      <footer style={{ borderTop:`1px solid rgba(255,107,53,0.1)`, padding: isMobile ? '24px 0' : '36px 0', background:'rgba(0,0,0,0.25)' }}>
        <div style={{ maxWidth:'1280px', margin:'0 auto', padding:'0 16px', display:'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent:'space-between', alignItems:'center', gap:'16px' }}>
          {/* Logo no footer */}
          <img src={LOGO_URL} alt="Luminê" style={{ height:'80px', width:'auto', filter:'brightness(0) invert(1)', opacity:0.5 }} />

          <div style={{ fontFamily:F.mono, fontSize:'11px', color:'rgba(255,255,255,0.22)', textAlign: isMobile ? 'center' : 'left' }}>
            © 2026 Luminê Telecom. Todos os direitos reservados.
          </div>
          <div style={{ display:'flex', gap:'16px', alignItems:'center', flexWrap:'wrap', justifyContent: isMobile ? 'center' : 'flex-start' }}>
            <a href={TC_WA_JOAO} target="_blank" rel="noopener noreferrer" style={{ fontFamily:F.mono, fontSize:'11px', color:'rgba(255,165,0,0.45)', textDecoration:'none', display:'flex', alignItems:'center', gap:'4px' }}>
              <Phone style={{ width:'10px', height:'10px' }}/> João: (91) 99983-0601
            </a>
            <a href={TC_WA_TIAGO} target="_blank" rel="noopener noreferrer" style={{ fontFamily:F.mono, fontSize:'11px', color:'rgba(255,165,0,0.45)', textDecoration:'none', display:'flex', alignItems:'center', gap:'4px' }}>
              <Phone style={{ width:'10px', height:'10px' }}/> Tiago: (91) 99208-5291
            </a>
            <a href="mailto:gestoradetelecom@lumine-ltda.com" style={{ fontFamily:F.mono, fontSize:'11px', color:'rgba(255,165,0,0.45)', textDecoration:'none' }}>
              gestoradetelecom@lumine-ltda.com
            </a>
            <button onClick={onSwitch} style={{ background:'none', border:'1px solid rgba(255,107,53,0.18)', borderRadius:'4px', padding:'5px 10px', color:'rgba(255,165,0,0.35)', cursor:'pointer', fontFamily:F.mono, fontSize:'10px' }}>
              Iluminação Pública
            </button>
          </div>
          <WeblyticsBadge />
        </div>
      </footer>

      {/* ── CHAT FLUTUANTE ─────────────────────────────────────── */}
      <div style={{ position:'fixed', bottom:'24px', right:'16px', zIndex:50 }}>
        {chatOpen && (
          <div style={{ position:'absolute', bottom:'62px', right:0, width: isMobile ? '280px' : '310px', background:B.gray900, border:'1px solid rgba(255,107,53,0.25)', borderRadius:'10px', overflow:'hidden', boxShadow:`0 0 40px rgba(255,107,53,0.1)` }}>
            <div style={{ padding:'12px 16px', background:'rgba(255,107,53,0.08)', borderBottom:'1px solid rgba(255,107,53,0.12)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontFamily:F.display, fontSize:'13px', fontWeight:600, color:B.orange }}>Assistente Luminê</span>
              <button onClick={() => setChatOpen(false)} style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(255,165,0,0.45)' }}>
                <X style={{ width:'13px', height:'13px' }}/>
              </button>
            </div>
            <div style={{ padding:'14px', maxHeight:'220px', overflowY:'auto', display:'flex', flexDirection:'column', gap:'8px' }}>
              {chatMsgs.map((m,i) => (
                <div key={i} style={{ fontFamily:F.body, fontSize:'12px', color: m.from==='bot' ? B.orange : 'rgba(255,255,255,0.6)', textAlign: m.from==='user' ? 'right' : 'left', fontStyle:'italic' }}>{m.text}</div>
              ))}
              <div ref={msgEnd}/>
            </div>
            <div style={{ padding:'10px', borderTop:'1px solid rgba(255,107,53,0.08)', display:'flex', flexDirection:'column', gap:'5px' }}>
              {chatStep === 'menu'
                ? [{k:'prazo',l:'Qual o prazo de execução?'},{k:'anatel',l:'Como funciona a aprovação?'},{k:'orcamento',l:'Solicitar orçamento'}].map(o => (
                    <button key={o.k} onClick={() => chatAct(o.k)} style={{ background:'rgba(255,107,53,0.06)', border:'1px solid rgba(255,107,53,0.18)', borderRadius:'4px', padding:'7px 10px', color:'rgba(255,165,0,0.75)', fontFamily:F.body, fontSize:'12px', cursor:'pointer', textAlign:'left' }}>{o.l}</button>
                  ))
                : (
                  <>
                    <button onClick={() => chatAct('wa')} style={{ background:B.grad, border:'none', borderRadius:'5px', padding:'9px', color:B.white, fontFamily:F.body, fontSize:'12px', cursor:'pointer', fontWeight:600 }}>Falar com Especialista</button>
                    <button onClick={() => chatAct('back')} style={{ background:'transparent', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'3px', padding:'7px', color:'rgba(255,255,255,0.3)', fontFamily:F.body, fontSize:'12px', cursor:'pointer' }}>Voltar</button>
                  </>
                )}
            </div>
          </div>
        )}
        <button onClick={() => setChatOpen(!chatOpen)} style={{ width:'48px', height:'48px', borderRadius:'50%', cursor:'pointer', background:'rgba(255,107,53,0.12)', border:'1px solid rgba(255,107,53,0.38)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:B.shadow }}>
          {chatOpen ? <X style={{ color:B.orange, width:'18px', height:'18px' }}/> : <MessageCircle style={{ color:B.orange, width:'18px', height:'18px' }}/>}
        </button>
      </div>

      {scrolled && !isMobile && (
        <button onClick={() => window.scrollTo({ top:0, behavior:'smooth' })} style={{ position:'fixed', bottom:'24px', left:'24px', zIndex:50, width:'38px', height:'38px', borderRadius:'6px', cursor:'pointer', background:'rgba(255,107,53,0.08)', border:'1px solid rgba(255,107,53,0.28)', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <ChevronDown style={{ color:B.orange, width:'15px', height:'15px', transform:'rotate(180deg)' }}/>
        </button>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
//  ILUMINAÇÃO SITE
// ════════════════════════════════════════════════════════════════
const IluminacaoSite = ({ onSwitch }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatStep, setChatStep] = useState('menu');
  const [chatMsgs, setChatMsgs] = useState([{ from:'bot', text:'Bem-vindo à Luminê. Como podemos auxiliar seu município?' }]);
  const [isMobile, setIsMobile] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const msgEnd = useRef(null);
  const WA = 'Olá! Gostaria de uma auditoria completa da iluminação pública do meu município.';
  const videoSrc = 'https://cdn.pixabay.com/video/2026/02/12/334215.mp4';

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (msgEnd.current) msgEnd.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMsgs]);

  const scrollTo = useCallback((id) => {
    setMenuOpen(false);
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }, 100);
  }, []);

  const addMsg = useCallback((from, text, delay = 0) => {
    setTimeout(() => setChatMsgs(prev => [...prev, { from, text }]), delay);
  }, []);

  const chatAct = useCallback((key) => {
    if (key === 'censo') {
      addMsg('user', 'O que é o censo de iluminação?');
      addMsg('bot', 'Levantamento com fotos georreferenciadas e evidências para atualização cadastral junto à distribuidora.', 500);
      setChatStep('fu');
    } else if (key === 'fatura') {
      addMsg('user', 'Como funciona a auditoria de fatura?');
      addMsg('bot', 'Análise mensal da fatura CIP para identificar cobranças indevidas. Municípios recuperam valores significativos retroativamente.', 500);
      setChatStep('fu');
    } else if (key === 'economia') {
      addMsg('user', 'Qual a economia estimada?');
      addMsg('bot', 'Redução de 30 a 40% nos custos e recuperação de créditos dos últimos 5 anos.', 500);
      setChatStep('fu');
    } else if (key === 'wa') {
      addMsg('user', 'Falar com especialista');
      addMsg('bot', 'Conectando ao time técnico...', 500);
      setTimeout(() => { window.open(`https://wa.me/5591999830601?text=${encodeURIComponent(WA)}`, '_blank'); }, 1200);
    } else if (key === 'back') {
      addMsg('user', 'Voltar ao menu');
      addMsg('bot', 'Como podemos ajudar?', 300);
      setChatStep('menu');
    }
  }, [addMsg, WA]);

  const services = useMemo(() => [
    { icon: ScanLine,   title: 'Censo de Iluminação',      desc: 'Levantamento com fotos e evidências para atualização cadastral junto à distribuidora',                         tags: ['Fotos 4K','Georreferenciamento','App Mobile'] },
    { icon: Globe2,     title: 'Sistema GIS Integrado',    desc: 'Plataforma integrada ao sistema da distribuidora para atualização cadastral em lote',                           tags: ['API','3D','Exportação'] },
    { icon: GitMerge,   title: 'Tratativas com Distribuidora', desc: 'Elaboração de documentos, respostas técnicas e acompanhamento de processos formais',                       tags: ['Documentos','Protocolos','Tracking'] },
    { icon: Receipt,    title: 'Gestão da Fatura CIP',     desc: 'Análise mensal com identificação de cobranças indevidas e duplicidades',                                         tags: ['Mensal','Relatórios','Conformidade'] },
    { icon: BarChart,   title: 'Gestão de Consumo',        desc: 'Apuração precisa em KWh para garantir que o município pague apenas o valor correto',                             tags: ['KWh','Comparativo','Projeções'] },
    { icon: FileSearch, title: 'Auditoria de Arrecadação', desc: 'Verificação da arrecadação CIP, identificação de desvios e recuperação de créditos',                            tags: ['Conciliação','Créditos','Relatório'] },
    { icon: Zap,        title: 'Retrofit LED Inteligente', desc: 'Modernização com LED e IoT para dimerização automatizada — redução de até 70%',                                  tags: ['LED','IoT','Dimerização'] },
    { icon: Shield,     title: 'Conformidade Técnica',     desc: 'ART, CREA, ABNT e NR-10 — documentação técnica completa e regularizada',                                         tags: ['ART','ABNT','NR-10'] },
  ], []);

  const process = useMemo(() => [
    { n:'01', title:'Diagnóstico Inicial',    desc:'Análise gratuita de cadastro, fatura e consumo do município',                   time:'3–5 dias',    icon:FileSearch },
    { n:'02', title:'Censo de Campo',          desc:'Levantamento georreferenciado com aplicativo móvel dedicado',                    time:'2–4 semanas', icon:MapPinned  },
    { n:'03', title:'Processamento GIS',       desc:'Integração com sistema da distribuidora via API certificada',                   time:'1–2 semanas', icon:Globe2     },
    { n:'04', title:'Auditoria de Fatura',     desc:'Análise detalhada de faturas e identificação de irregularidades',               time:'2–3 semanas', icon:FileSearch },
    { n:'05', title:'Tratativas Técnicas',     desc:'Comunicação formal e acompanhamento junto à concessionária',                    time:'1–3 semanas', icon:GitMerge   },
    { n:'06', title:'Gestão Continuada',       desc:'Monitoramento mensal permanente com relatórios periódicos',                     time:'Contínuo',    icon:Activity   },
  ], []);

  const cases = useMemo(() => [
    { city:'Prefeituras do Pará', cat:'Auditoria CIP', headline:'R$ 400k/mês', desc:'Redução de R$ 400 mil mensais nos custos com iluminação pública após auditoria completa e regularização cadastral.', kpis:[['R$ 400k','Mensal'],['R$ 4,8M','Anual'],['38%','Redução']] },
  ], []);

  const faqs = useMemo(() => [
    { q:'O que é o censo de iluminação pública?',                    a:'Levantamento técnico completo com fotos georreferenciadas. As evidências são utilizadas para atualização cadastral junto à distribuidora — o município passa a pagar apenas pelos pontos efetivamente existentes.' },
    { q:'Qual a diferença entre gestão de fatura e auditoria de arrecadação?', a:'A gestão de fatura analisa mensalmente os valores cobrados pela distribuidora. A auditoria de arrecadação verifica a CIP arrecadada dos contribuintes. As duas ações são complementares.' },
    { q:'Em quanto tempo os resultados são visíveis?',               a:'Auditoria de faturas: 30 a 60 dias. Recuperação de créditos: 3 a 6 meses. Censo completo: 3 a 5 meses.' },
    { q:'Como são conduzidas as tratativas com a distribuidora?',    a:'Gerenciamos todos os processos formais — desde protocolos de atualização cadastral até recursos administrativos contra cobranças indevidas.' },
    { q:'O que é a CIP?',                                            a:'Contribuição de Iluminação Pública — tarifa destinada ao custeio da iluminação municipal. Auditamos tanto os valores cobrados quanto a arrecadação desta contribuição.' },
  ], []);

  const IServiceCard = useCallback(({ s, i }) => {
    const Icon = s.icon;
    const [ref, inView] = useInView();
    const [hov, setHov] = useState(false);
    return (
      <div ref={ref} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        style={{ background: hov ? `linear-gradient(135deg,rgba(255,107,53,0.025),rgba(255,165,0,0.025))` : B.white, border:`2px solid rgba(255,107,53,${hov ? 0.3 : 0.09})`, borderRadius:'16px', padding: isMobile ? '20px' : '28px', transition:'all 0.3s', boxShadow: hov ? B.shadowL : '0 2px 8px rgba(0,0,0,0.03)', transform: hov ? 'translateY(-4px)' : 'none', opacity: inView ? 1 : 0, animationName: inView ? 'ilfade' : 'none', animationDuration:'0.5s', animationTimingFunction:'ease-out', animationFillMode:'both', animationDelay:`${i*55}ms` }}
      >
        <div style={{ width: isMobile ? '40px' : '48px', height: isMobile ? '40px' : '48px', borderRadius:'12px', background: hov ? B.grad : 'rgba(255,107,53,0.08)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'18px', transition:'all 0.3s', boxShadow: hov ? B.shadow : 'none' }}>
          <Icon style={{ width: isMobile ? '18px' : '20px', height: isMobile ? '18px' : '20px', color: hov ? B.white : B.orange }}/>
        </div>
        <h3 style={{ fontFamily:F.display, fontWeight:600, fontSize: isMobile ? '15px' : '16px', color: hov ? B.orange : B.gray900, marginBottom:'9px', transition:'color 0.3s' }}>{s.title}</h3>
        <p style={{ fontSize:'13px', color:B.gray600, lineHeight:1.75, marginBottom:'16px', fontFamily:F.body, fontStyle:'italic' }}>{s.desc}</p>
        <div style={{ display:'flex', gap:'7px', flexWrap:'wrap' }}>
          {s.tags.map(t => (<span key={t} style={{ fontSize:'11px', padding:'4px 11px', borderRadius:'4px', background:'rgba(255,107,53,0.06)', border:`1px solid rgba(255,107,53,0.15)`, color:B.orange, fontFamily:F.mono, fontWeight:500 }}>{t}</span>))}
        </div>
      </div>
    );
  }, [isMobile]);

  const IProcessCard = useCallback(({ st, i }) => {
    const Icon = st.icon;
    const [ref, inView] = useInView();
    return (
      <div ref={ref} style={{ background:B.white, border:`1px solid rgba(255,107,53,0.1)`, borderRadius:'16px', padding: isMobile ? '20px' : '28px', opacity: inView ? 1 : 0, transition:'all 0.5s', transitionDelay:`${i*70}ms`, transform: inView ? 'none' : 'translateY(20px)', boxShadow:'0 2px 10px rgba(255,107,53,0.04)' }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'18px' }}>
          <div style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? '34px' : '44px', color:'rgba(255,107,53,0.08)', lineHeight:1 }}>{st.n}</div>
          <div style={{ width: isMobile ? '36px' : '40px', height: isMobile ? '36px' : '40px', borderRadius:'10px', background:B.grad, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:B.shadow }}>
            <Icon style={{ width: isMobile ? '16px' : '18px', height: isMobile ? '16px' : '18px', color:B.white }}/>
          </div>
        </div>
        <h3 style={{ fontFamily:F.display, fontWeight:600, fontSize: isMobile ? '15px' : '16px', color:B.gray900, marginBottom:'8px' }}>{st.title}</h3>
        <p style={{ fontSize:'13px', color:B.gray600, lineHeight:1.75, marginBottom:'14px', fontFamily:F.body, fontStyle:'italic' }}>{st.desc}</p>
        <div style={{ display:'inline-flex', alignItems:'center', gap:'5px', padding:'5px 12px', borderRadius:'100px', background:'rgba(255,107,53,0.06)', border:`1px solid rgba(255,107,53,0.13)` }}>
          <Clock style={{ width:'11px', height:'11px', color:B.orange }}/>
          <span style={{ fontSize:'12px', color:B.orange, fontWeight:600, fontFamily:F.mono }}>{st.time}</span>
        </div>
      </div>
    );
  }, [isMobile]);

  const ICaseCard = useCallback(({ c, i }) => {
    const [ref, inView] = useInView();
    return (
      <div ref={ref} style={{ background:B.white, border:`2px solid rgba(255,107,53,0.09)`, borderRadius:'20px', padding: isMobile ? '24px' : '36px', opacity: inView ? 1 : 0, transition:'all 0.5s', transitionDelay:`${i*90}ms`, transform: inView ? 'none' : 'translateY(20px)', boxShadow:'0 4px 20px rgba(255,107,53,0.05)' }}>
        <div style={{ display:'inline-block', padding:'4px 13px', borderRadius:'100px', background:'rgba(255,107,53,0.07)', border:`1px solid rgba(255,107,53,0.15)`, marginBottom:'14px' }}>
          <span style={{ fontSize:'11px', color:B.orange, fontWeight:600, fontFamily:F.mono }}>{c.cat}</span>
        </div>
        <h3 style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? '17px' : '19px', color:B.gray900, marginBottom:'8px' }}>{c.city}</h3>
        <div style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? '24px' : '28px', background:B.grad, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text', letterSpacing:'-0.01em', marginBottom:'12px' }}>{c.headline}</div>
        <p style={{ fontSize:'13px', color:B.gray600, lineHeight:1.75, marginBottom:'20px', fontFamily:F.body, fontStyle:'italic' }}>{c.desc}</p>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', borderTop:`1px solid rgba(255,107,53,0.09)`, paddingTop:'18px', gap:'12px' }}>
          {c.kpis.map(([v,l],j) => (
            <div key={j} style={{ textAlign:'center' }}>
              <div style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? '14px' : '16px', color:B.gray900 }}>{v}</div>
              <div style={{ fontSize:'10px', color:B.gray400, marginTop:'2px', fontFamily:F.mono }}>{l}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }, [isMobile]);

  const IFaqItem = useCallback(({ it }) => {
    const [open, setOpen] = useState(false);
    return (
      <div style={{ background:B.white, borderRadius:'12px', border:`1px solid rgba(255,107,53,${open ? 0.28 : 0.1})`, overflow:'hidden', transition:'border-color 0.3s', boxShadow: open ? `0 4px 16px rgba(255,107,53,0.07)` : '' }}>
        <button onClick={() => setOpen(!open)} style={{ width:'100%', padding: isMobile ? '16px 18px' : '20px 22px', display:'flex', justifyContent:'space-between', alignItems:'center', background:'none', border:'none', cursor:'pointer', textAlign:'left' }}>
          <span style={{ fontFamily:F.display, fontWeight:600, fontSize: isMobile ? '14px' : '15px', color: open ? B.orange : B.gray900, paddingRight:'16px', transition:'color 0.3s' }}>{it.q}</span>
          <ChevronDown style={{ color:B.orange, width:'17px', height:'17px', flexShrink:0, transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.3s' }}/>
        </button>
        {open && (
          <div style={{ padding: isMobile ? '0 18px 16px' : '0 22px 20px', fontSize: isMobile ? '13px' : '14px', color:B.gray600, lineHeight:1.85, fontFamily:F.body, fontStyle:'italic' }}>{it.a}</div>
        )}
      </div>
    );
  }, [isMobile]);

  return (
    <div style={{ background:B.white, color:B.gray900, fontFamily:F.body, minHeight:'100vh' }}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes ilfade{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
        ${FONTS_STYLE}
      `}</style>
      <SecurityGuard />
      <ScrollBar />

      {/* ── HEADER ─────────────────────────────────────────────── */}
      <header style={{ position:'fixed', top:'2px', left:0, right:0, zIndex:100, height:'76px', background: scrolled ? 'rgba(255,255,255,0.97)' : 'rgba(255,255,255,0.88)', backdropFilter:'blur(20px)', borderBottom:`1px solid ${scrolled ? 'rgba(255,107,53,0.12)' : 'transparent'}`, transition:'all 0.3s', display:'flex', alignItems:'center' }}>
        <div style={{ maxWidth:'1280px', margin:'0 auto', padding: isMobile ? '0 16px' : '0 32px', width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between' }}>

          {/* Logo */}
          <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
            <img
              src={LOGO_URL}
              alt="Luminê"
              style={{ height: isMobile ? '90px' : '90px', width:'auto' }}
            />
          </div>

          <nav style={{ display:'flex', alignItems:'center', gap: isMobile ? '8px' : '32px' }}>
            {!isMobile && [{id:'processo',l:'Metodologia'},{id:'cases',l:'Cases'},{id:'faq',l:'FAQ'}].map(({id,l}) => (
              <button key={id} onClick={() => scrollTo(id)}
                style={{ background:'none', border:'none', cursor:'pointer', color:B.gray600, fontSize:'14px', fontWeight:500, fontFamily:F.body, padding:'4px 0', borderBottom:'2px solid transparent', transition:'all 0.25s' }}
                onMouseEnter={e => { e.target.style.color=B.orange; e.target.style.borderBottomColor=B.orange; }}
                onMouseLeave={e => { e.target.style.color=B.gray600; e.target.style.borderBottomColor='transparent'; }}
              >{l}</button>
            ))}
            <button onClick={onSwitch}
              style={{ background:'none', border:`1px solid rgba(255,107,53,0.28)`, borderRadius:'6px', padding: isMobile ? '4px 8px' : '6px 14px', cursor:'pointer', color:B.gray600, fontSize: isMobile ? '12px' : '13px', fontFamily:F.body, transition:'all 0.25s' }}
              onMouseEnter={e => { e.target.style.background='rgba(255,107,53,0.05)'; e.target.style.color=B.orange; }}
              onMouseLeave={e => { e.target.style.background='none'; e.target.style.color=B.gray600; }}
            >
              {isMobile ? '⇄' : 'Telecomunicações'}
            </button>
            {!isMobile && (
              <a href={`https://wa.me/5591999830601?text=${encodeURIComponent(WA)}`}
                style={{ display:'flex', alignItems:'center', gap:'8px', padding:'12px 22px', borderRadius:'10px', textDecoration:'none', background:B.grad, color:B.white, fontWeight:600, fontSize:'14px', fontFamily:F.body, boxShadow:B.shadow }}
              >
                <Phone style={{ width:'14px', height:'14px' }}/> Falar com Especialista
              </a>
            )}
          </nav>

          {isMobile && (
            <button onClick={() => setMenuOpen(!menuOpen)} style={{ background:'none', border:`1px solid rgba(255,107,53,0.22)`, borderRadius:'8px', padding:'8px', cursor:'pointer', display:'flex' }}>
              <Menu style={{ color:B.orange, width:'20px', height:'20px' }}/>
            </button>
          )}
        </div>

        {menuOpen && (
          <div style={{ position:'absolute', top:'100%', left:0, right:0, background:B.white, borderBottom:`1px solid rgba(255,107,53,0.1)`, padding:'16px', display:'flex', flexDirection:'column', gap:'4px', boxShadow:`0 8px 32px rgba(0,0,0,0.06)` }}>
            {[{id:'servicos',l:'Serviços'},{id:'processo',l:'Metodologia'},{id:'cases',l:'Cases'},{id:'faq',l:'FAQ'}].map(({id,l}) => (
              <button key={id} onClick={() => scrollTo(id)} style={{ background:'none', border:'none', cursor:'pointer', textAlign:'left', color:B.gray800, padding:'12px 0', fontSize:'15px', fontFamily:F.body, borderBottom:`1px solid ${B.gray100}` }}>{l}</button>
            ))}
            <button onClick={() => { onSwitch(); setMenuOpen(false); }} style={{ background:'none', border:'none', cursor:'pointer', textAlign:'left', color:B.orange, padding:'12px 0', fontSize:'14px', fontFamily:F.body }}>
              Mudar para Telecomunicações
            </button>
            <a href={`https://wa.me/5591999830601?text=${encodeURIComponent(WA)}`} style={{ marginTop:'8px', textAlign:'center', padding:'14px', background:B.grad, borderRadius:'10px', color:B.white, textDecoration:'none', fontWeight:600, fontFamily:F.body }}>
              Falar com Especialista
            </a>
          </div>
        )}
      </header>

      {/* ── HERO ───────────────────────────────────────────────── */}
      <section style={{ position:'relative', minHeight:'100vh', display:'flex', alignItems:'center', paddingTop:'76px', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0 }}>
          {!videoLoaded && (
            <div style={{ position:'absolute', inset:0, background:'linear-gradient(135deg,#fff8f5 0%,#fff 50%,#fffbf0 100%)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <div style={{ width:'40px', height:'40px', borderRadius:'50%', border:'2px solid rgba(255,107,53,0.18)', borderTopColor:B.orange, animation:'spin 1s linear infinite' }}/>
            </div>
          )}
          <video autoPlay loop muted playsInline onLoadedData={() => setVideoLoaded(true)}
            style={{ position:'absolute', width:'100%', height:'100%', objectFit:'cover', opacity: videoLoaded ? 1 : 0, transition:'opacity 0.8s', filter:'brightness(0.9)' }}
          >
            <source src={videoSrc} type="video/mp4"/>
          </video>
          <div style={{ position:'absolute', inset:0, background:'linear-gradient(135deg,rgba(255,255,255,0.96) 0%,rgba(255,255,255,0.88) 0%,rgba(255,255,255,0.96) 100%)' }}/>
        </div>
        <div style={{ position:'relative', zIndex:2, maxWidth:'1280px', margin:'0 auto', padding: isMobile ? '0 16px' : '0 32px', width:'100%' }}>
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '32px' : '80px', alignItems:'center' }}>
            <div>
              <div style={{ display:'inline-flex', alignItems:'center', gap:'8px', padding:'8px 18px', borderRadius:'100px', background:'rgba(255,107,53,0.07)', border:`1px solid rgba(255,107,53,0.18)`, marginBottom:'32px', fontSize:'13px', color:B.orange, fontWeight:600, fontFamily:F.body }}>
                <Lightbulb style={{ width:'14px', height:'14px' }}/>
                {isMobile ? 'Gestão Municipal' : 'Especialistas em Gestão Municipal'}
              </div>
              <h1 style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? 'clamp(34px,9vw,46px)' : 'clamp(40px,5vw,72px)', lineHeight:1.05, letterSpacing:'-0.02em', marginBottom:'24px', color:B.gray900 }}>
                Gestão<br/><span style={{ background:B.grad, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>Inteligente</span><br/>de Iluminação<br/><span style={{ color:B.gray400 }}>Pública</span>
              </h1>
              <p style={{ fontSize: isMobile ? '15px' : '17px', color:B.gray600, lineHeight:1.85, marginBottom:'36px', maxWidth:'460px', fontFamily:F.body, fontStyle:'italic' }}>
                Censo, GIS, auditoria de fatura CIP e tratativas com distribuidora. Reduza custos e recupere o que seu município tem direito.
              </p>
              <div style={{ display:'flex', gap:'14px', flexWrap:'wrap', marginBottom:'40px' }}>
                <a href={`https://wa.me/5591999830601?text=${encodeURIComponent(WA)}`}
                  style={{ display:'flex', alignItems:'center', gap:'10px', padding: isMobile ? '12px 20px' : '15px 30px', borderRadius:'12px', textDecoration:'none', background:B.grad, color:B.white, fontWeight:600, fontSize: isMobile ? '14px' : '15px', fontFamily:F.body, boxShadow:B.shadowL }}
                >
                  <MessageCircle style={{ width:'17px', height:'17px' }}/>
                  {isMobile ? 'Auditoria Gratuita' : 'Solicitar Auditoria Gratuita'}
                </a>
                {!isMobile && (
                  <button onClick={() => scrollTo('processo')}
                    style={{ display:'flex', alignItems:'center', gap:'8px', padding:'15px 24px', borderRadius:'12px', background:B.white, border:`2px solid rgba(255,107,53,0.18)`, color:B.gray800, fontWeight:600, fontSize:'14px', fontFamily:F.body, cursor:'pointer' }}
                  >
                    Ver Soluções <ChevronDown style={{ width:'15px', height:'15px' }}/>
                  </button>
                )}
              </div>
            </div>
            {!isMobile && (
              <div style={{ position:'relative', height:'460px' }}>
                <div style={{ position:'absolute', inset:0, backgroundImage:`linear-gradient(rgba(255,107,53,0.06) 1px,transparent 1px),linear-gradient(90deg,rgba(255,107,53,0.06) 1px,transparent 1px)`, backgroundSize:'44px 44px', borderRadius:'24px' }}/>
                {[
                  { top:'8%', left:'8%',   val:'40%',      lbl:'Redução de custos', big:true, grad:true },
                  { top:'42%', right:'5%', val:'R$ 7M+',   lbl:'Economia',          big:true },
                  { bottom:'8%', left:'12%', val:'+18%',   lbl:'Aumento da CIP',    invert:true },
                  { top:'20%', right:'18%',  val:'GEOS',   lbl:'Sistema ANATEL',    small:true }
                ].map((c, i) => (
                  <div key={i} style={{ position:'absolute', top:c.top, left:c.left, right:c.right, bottom:c.bottom, background: c.invert ? B.grad : B.white, borderRadius:'16px', padding:'20px 24px', boxShadow: c.invert ? B.shadowL : `0 8px 32px rgba(255,107,53,0.1)`, border: c.invert ? 'none' : `1px solid rgba(255,107,53,0.12)`, minWidth:'150px' }}>
                    <div style={{ fontFamily:F.display, fontWeight:700, fontSize: c.small ? '22px' : '34px', color: c.invert ? B.white : c.grad ? 'transparent' : B.gray900, background: c.grad ? B.grad : 'none', WebkitBackgroundClip: c.grad ? 'text' : 'unset', WebkitTextFillColor: c.grad ? 'transparent' : 'unset', backgroundClip: c.grad ? 'text' : 'unset' }}>{c.val}</div>
                    <div style={{ fontSize: c.small ? '11px' : '13px', color: c.invert ? 'rgba(255,255,255,0.78)' : B.gray600, marginTop:'4px', fontFamily:F.body, fontStyle:'italic' }}>{c.lbl}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── STATS ──────────────────────────────────────────────── */}
      <section style={{ background:B.gray900, padding: isMobile ? '32px 0' : '52px 0' }}>
        <div style={{ maxWidth:'1280px', margin:'0 auto', padding:'0 16px' }}>
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(auto-fit,minmax(200px,1fr))', gap:'1px', background:'rgba(255,255,255,0.05)' }}>
            {[
              { icon:DollarSign, v:'R$ 7M+',     t:'Economia',              d:'gerados para os municípios parceiros' },
              { icon:Coins,      v:'Até 5 anos',  t:'Recuperação Retroativa', d:'de cobranças indevidas' },
              { icon:TrendingUp, v:'9 a 18%',     t:'Aumento da CIP',        d:'na arrecadação municipal' },
              { icon:Cpu,        v:'GEOS',         t:'Sistema ANATEL',        d:'indicado para utilização das distribuidoras' }
            ].map(({ icon:Ic, v, t, d }, i) => (
              <div key={i} style={{ background:'rgba(255,255,255,0.035)', border:'1px solid rgba(255,107,53,0.18)', borderRadius:'14px', padding: isMobile ? '20px' : '28px', textAlign:'center' }}>
                <div style={{ width:'48px', height:'48px', borderRadius:'50%', background:'rgba(255,107,53,0.1)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
                  <Ic style={{ width:'20px', height:'20px', color:B.amber }}/>
                </div>
                <div style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? '20px' : '24px', background:B.grad, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text', marginBottom:'6px' }}>{v}</div>
                <div style={{ fontFamily:F.display, fontWeight:600, fontSize:'14px', color:B.white, marginBottom:'4px' }}>{t}</div>
                <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.38)', fontFamily:F.body, fontStyle:'italic' }}>{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESSO ───────────────────────────────────────────── */}
      <section id="processo" style={{ padding: isMobile ? '60px 0' : '100px 0', background:B.gray50 }}>
        <div style={{ maxWidth:'1100px', margin:'0 auto', padding:'0 16px' }}>
          <div style={{ textAlign:'center', marginBottom: isMobile ? '40px' : '64px' }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:'8px', padding:'8px 18px', borderRadius:'100px', background:'rgba(255,107,53,0.07)', border:`1px solid rgba(255,107,53,0.16)`, marginBottom:'20px' }}>
              <Activity style={{ width:'13px', height:'13px', color:B.orange }}/>
              <span style={{ fontSize:'13px', color:B.orange, fontWeight:600, fontFamily:F.body }}>Metodologia</span>
            </div>
            <h2 style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? 'clamp(26px,7vw,34px)' : 'clamp(30px,3.5vw,50px)', letterSpacing:'-0.01em', color:B.gray900, lineHeight:1.15 }}>
              Como <span style={{ background:B.grad, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>Trabalhamos</span>
            </h2>
            {!isMobile && <p style={{ color:B.gray400, fontSize:'15px', marginTop:'12px', fontFamily:F.body, fontStyle:'italic' }}>Seis etapas com prazos garantidos em contrato</p>}
          </div>
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(300px,1fr))', gap:'20px' }}>
            {process.map((st,i) => <IProcessCard key={st.n} st={st} i={i} />)}
          </div>
        </div>
      </section>

      {/* ── CASES ──────────────────────────────────────────────── */}
      <section id="cases" style={{ padding: isMobile ? '60px 0' : '100px 0', background:B.white }}>
        <div style={{ maxWidth:'1280px', margin:'0 auto', padding:'0 16px' }}>
          <div style={{ textAlign:'center', marginBottom: isMobile ? '40px' : '64px' }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:'8px', padding:'8px 18px', borderRadius:'100px', background:'rgba(255,107,53,0.07)', border:`1px solid rgba(255,107,53,0.16)`, marginBottom:'20px' }}>
              <Star style={{ width:'13px', height:'13px', color:B.orange }}/>
              <span style={{ fontSize:'13px', color:B.orange, fontWeight:600, fontFamily:F.body }}>Resultados Comprovados</span>
            </div>
            <h2 style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? 'clamp(26px,7vw,34px)' : 'clamp(30px,3.5vw,50px)', letterSpacing:'-0.01em', color:B.gray900, lineHeight:1.15 }}>
              Cases de <span style={{ background:B.grad, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>Referência</span>
            </h2>
          </div>
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(320px,1fr))', gap:'24px' }}>
            {cases.map((c,i) => <ICaseCard key={c.city} c={c} i={i} />)}
          </div>
        </div>
      </section>

      {/* ── POR QUE A LUMINÊ ───────────────────────────────────── */}
      <section style={{ padding: isMobile ? '40px 0' : '80px 0', background:B.gray900 }}>
        <div style={{ maxWidth:'1100px', margin:'0 auto', padding:'0 16px' }}>
          <h2 style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? 'clamp(22px,5vw,30px)' : 'clamp(26px,3vw,44px)', color:B.white, textAlign:'center', marginBottom: isMobile ? '32px' : '48px', letterSpacing:'-0.01em' }}>
            Por que Municípios <span style={{ background:B.grad, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>escolhem a Luminê</span>
          </h2>
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(220px,1fr))', gap:'20px' }}>
            {[
              { icon:DollarSign, v:'Até 40%',  t:'Redução de Custos',        d:'nos gastos com iluminação' },
              { icon:Coins,      v:'Até 5 anos', t:'Recuperação Retroativa', d:'de cobranças indevidas' },
              { icon:CheckSquare, v:'100%',     t:'Conformidade',             d:'com distribuidora e legislação' },
              { icon:Clock,      v:'6 meses',   t:'Retorno do Investimento',  d:'prazo médio de ROI' }
            ].map(({ icon:Ic, v, t, d }, i) => (
              <div key={i} style={{ background:'rgba(255,255,255,0.035)', border:'1px solid rgba(255,107,53,0.18)', borderRadius:'14px', padding: isMobile ? '20px' : '28px', textAlign:'center' }}>
                <div style={{ width:'48px', height:'48px', borderRadius:'50%', background:'rgba(255,107,53,0.1)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
                  <Ic style={{ width:'20px', height:'20px', color:B.amber }}/>
                </div>
                <div style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? '20px' : '24px', background:B.grad, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text', marginBottom:'6px' }}>{v}</div>
                <div style={{ fontFamily:F.display, fontWeight:600, fontSize:'14px', color:B.white, marginBottom:'4px' }}>{t}</div>
                <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.38)', fontFamily:F.body, fontStyle:'italic' }}>{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ────────────────────────────────────────────────── */}
      <section id="faq" style={{ padding: isMobile ? '60px 0' : '100px 0', background:B.gray50 }}>
        <div style={{ maxWidth: isMobile ? '100%' : '780px', margin:'0 auto', padding:'0 16px' }}>
          <div style={{ textAlign:'center', marginBottom: isMobile ? '40px' : '56px' }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:'8px', padding:'8px 18px', borderRadius:'100px', background:'rgba(255,107,53,0.07)', border:`1px solid rgba(255,107,53,0.16)`, marginBottom:'20px' }}>
              <BookMarked style={{ width:'13px', height:'13px', color:B.orange }}/>
              <span style={{ fontSize:'13px', color:B.orange, fontWeight:600, fontFamily:F.body }}>Perguntas Frequentes</span>
            </div>
            <h2 style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? 'clamp(26px,7vw,34px)' : 'clamp(30px,3.5vw,46px)', letterSpacing:'-0.01em', color:B.gray900 }}>
              Perguntas <span style={{ background:B.grad, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>Frequentes</span>
            </h2>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
            {faqs.map((it,i) => <IFaqItem key={i} it={it} />)}
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ──────────────────────────────────────────── */}
      <section style={{ padding: isMobile ? '60px 0' : '100px 0', background:B.grad, position:'relative', overflow:'hidden', textAlign:'center' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:`radial-gradient(circle,rgba(255,255,255,0.03) 1px,transparent 1px)`, backgroundSize:'28px 28px' }}/>
        <div style={{ position:'relative', zIndex:1, maxWidth:'680px', margin:'0 auto', padding:'0 16px' }}>
          <div style={{ fontSize:'13px', color:'rgba(255,255,255,0.65)', fontFamily:F.body, fontStyle:'italic', marginBottom:'20px' }}>Resultados visíveis em 30 dias</div>
          <h2 style={{ fontFamily:F.display, fontWeight:700, fontSize: isMobile ? 'clamp(26px,6vw,34px)' : 'clamp(32px,5vw,60px)', color:B.white, letterSpacing:'-0.02em', lineHeight:1.05, marginBottom:'20px' }}>
            Transforme a Gestão de<br/>Iluminação do seu Município
          </h2>
          <p style={{ color:'rgba(255,255,255,0.72)', fontSize: isMobile ? '15px' : '17px', marginBottom:'36px', fontFamily:F.body, fontStyle:'italic' }}>
            Diagnóstico gratuito, sem compromisso. Retorno em até 2 horas úteis.
          </p>
          <a href={`https://wa.me/5591999830601?text=${encodeURIComponent(WA)}`}
            style={{ display:'inline-flex', alignItems:'center', gap:'12px', padding: isMobile ? '14px 24px' : '17px 40px', borderRadius:'12px', textDecoration:'none', background:B.white, color:B.orange, fontWeight:700, fontSize: isMobile ? '14px' : '16px', fontFamily:F.body, boxShadow:'0 8px 32px rgba(0,0,0,0.15)' }}
          >
            <Phone style={{ width:'19px', height:'19px' }}/>
            {isMobile ? 'Solicitar Auditoria' : 'Solicitar Auditoria Gratuita'}
            <ArrowRight style={{ width:'17px', height:'17px' }}/>
          </a>
        </div>
      </section>

      {/* ── FOOTER ─────────────────────────────────────────────── */}
      <footer style={{ background:B.gray900, padding: isMobile ? '32px 0' : '56px 0 40px', color:B.white }}>
        <div style={{ maxWidth:'1280px', margin:'0 auto', padding:'0 16px' }}>
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(220px,1fr))', gap: isMobile ? '24px' : '48px', marginBottom:'48px' }}>
            {/* Logo no footer */}
            <div>
              <div style={{ display:'flex', alignItems:'center', marginBottom:'14px', justifyContent: isMobile ? 'center' : 'flex-start' }}>
                <img src={LOGO_URL} alt="Luminê" style={{ height:'80px', width:'auto', filter:'brightness(0) invert(1)' }} />
              </div>
              <p style={{ color:'rgba(255,255,255,0.38)', fontSize:'14px', lineHeight:1.75, fontFamily:F.body, fontStyle:'italic', textAlign: isMobile ? 'center' : 'left' }}>
                Gestão especializada de iluminação pública para municípios brasileiros.
              </p>
            </div>
            <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
              <h4 style={{ fontFamily:F.display, fontWeight:600, marginBottom:'18px', fontSize:'14px', color:B.amber }}>Serviços</h4>
              {['Censo de Iluminação','Gestão de Fatura CIP','Auditoria de Consumo','Tratativas com Distribuidora'].map(s => (
                <div key={s} style={{ color:'rgba(255,255,255,0.35)', fontSize:'13px', marginBottom:'9px', fontFamily:F.body }}>{s}</div>
              ))}
            </div>
            <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
              <h4 style={{ fontFamily:F.display, fontWeight:600, marginBottom:'18px', fontSize:'14px', color:B.amber }}>Contato</h4>
              <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                <a href="https://wa.me/5591999830601" style={{ display:'flex', alignItems:'center', gap:'8px', color:'rgba(255,255,255,0.35)', fontSize:'13px', textDecoration:'none', fontFamily:F.body, justifyContent: isMobile ? 'center' : 'flex-start' }}>
                  <Phone style={{ width:'13px', height:'13px', color:B.orange }}/> João: (91) 99983-0601
                </a>
                <a href="https://wa.me/5591992085291" style={{ display:'flex', alignItems:'center', gap:'8px', color:'rgba(255,255,255,0.35)', fontSize:'13px', textDecoration:'none', fontFamily:F.body, justifyContent: isMobile ? 'center' : 'flex-start' }}>
                  <Phone style={{ width:'13px', height:'13px', color:B.orange }}/> Tiago: (91) 99208-5291
                </a>
                <a href="mailto:gestoradeIP@lumine-ltda.com" style={{ display:'flex', alignItems:'center', gap:'8px', color:'rgba(255,255,255,0.35)', fontSize:'13px', textDecoration:'none', fontFamily:F.body, justifyContent: isMobile ? 'center' : 'flex-start' }}>
                  <Mail style={{ width:'13px', height:'13px', color:B.orange }}/> gestoradeIP@lumine-ltda.com
                </a>
              </div>
            </div>
          </div>
          <div style={{ borderTop:'1px solid rgba(255,255,255,0.06)', paddingTop:'24px', display:'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent:'space-between', alignItems:'center', gap:'16px' }}>
            <div style={{ color:'rgba(255,255,255,0.22)', fontSize:'12px', fontFamily:F.mono }}>© 2026 Luminê Iluminação Pública. Todos os direitos reservados.</div>
            <button onClick={onSwitch} style={{ background:'none', border:'1px solid rgba(255,107,53,0.18)', borderRadius:'6px', padding:'5px 12px', color:'rgba(255,165,0,0.4)', cursor:'pointer', fontSize:'12px', fontFamily:F.mono }}>Telecomunicações</button>
            <WeblyticsBadge />
          </div>
        </div>
      </footer>

      {/* ── CHAT FLUTUANTE ─────────────────────────────────────── */}
      <div style={{ position:'fixed', bottom:'24px', right:'16px', zIndex:50 }}>
        {chatOpen && (
          <div style={{ position:'absolute', bottom:'66px', right:0, width: isMobile ? '280px' : '320px', background:B.white, border:`1px solid rgba(255,107,53,0.18)`, borderRadius:'16px', overflow:'hidden', boxShadow:B.shadowL }}>
            <div style={{ padding:'14px 16px', background:B.grad, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontFamily:F.display, fontWeight:600, color:B.white, fontSize:'14px' }}>Assistente Luminê</div>
                <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.7)', fontFamily:F.body, fontStyle:'italic' }}>Disponível agora</div>
              </div>
              <button onClick={() => setChatOpen(false)} style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(255,255,255,0.7)' }}>
                <X style={{ width:'15px', height:'15px' }}/>
              </button>
            </div>
            <div style={{ padding:'14px', maxHeight:'200px', overflowY:'auto', display:'flex', flexDirection:'column', gap:'10px' }}>
              {chatMsgs.map((m,i) => (
                <div key={i} style={{ padding:'10px 13px', borderRadius:'10px', fontSize: isMobile ? '12px' : '13px', maxWidth:'85%', lineHeight:1.6, alignSelf: m.from==='user' ? 'flex-end' : 'flex-start', background: m.from==='user' ? B.grad : `rgba(255,107,53,0.05)`, color: m.from==='user' ? B.white : B.gray800, border: m.from==='bot' ? `1px solid rgba(255,107,53,0.12)` : 'none', fontFamily:F.body, fontStyle: m.from==='bot' ? 'italic' : 'normal' }}>
                  {m.text}
                </div>
              ))}
              <div ref={msgEnd}/>
            </div>
            <div style={{ padding:'12px', borderTop:`1px solid rgba(255,107,53,0.09)`, display:'flex', flexDirection:'column', gap:'7px', background:B.gray50 }}>
              {chatStep === 'menu'
                ? [{ k:'censo', l:'O que é o censo de iluminação?' },{ k:'fatura', l:'Como funciona a auditoria de fatura?' },{ k:'economia', l:'Qual a economia estimada?' }].map(o => (
                    <button key={o.k} onClick={() => chatAct(o.k)} style={{ background:B.white, border:`1px solid rgba(255,107,53,0.18)`, borderRadius:'8px', padding:'9px 13px', color:B.gray800, fontSize: isMobile ? '12px' : '13px', cursor:'pointer', textAlign:'left', fontFamily:F.body }}>{o.l}</button>
                  ))
                : (
                  <>
                    <button onClick={() => chatAct('wa')} style={{ background:B.grad, border:'none', borderRadius:'8px', padding:'11px', color:B.white, fontWeight:600, fontSize: isMobile ? '12px' : '13px', cursor:'pointer', fontFamily:F.body }}>Falar com Especialista</button>
                    <button onClick={() => chatAct('back')} style={{ background:B.white, border:`1px solid rgba(255,107,53,0.14)`, borderRadius:'8px', padding:'9px', color:B.gray600, fontSize: isMobile ? '11px' : '12px', cursor:'pointer', fontFamily:F.body }}>Voltar ao início</button>
                  </>
                )}
            </div>
          </div>
        )}
        <button onClick={() => setChatOpen(!chatOpen)} style={{ width: isMobile ? '48px' : '52px', height: isMobile ? '48px' : '52px', borderRadius:'50%', cursor:'pointer', border:'none', background:B.grad, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:B.shadowL }}>
          {chatOpen ? <X style={{ color:B.white, width:'20px', height:'20px' }}/> : <MessageCircle style={{ color:B.white, width:'20px', height:'20px' }}/>}
        </button>
      </div>

      {scrolled && !isMobile && (
        <button onClick={() => window.scrollTo({ top:0, behavior:'smooth' })} style={{ position:'fixed', bottom:'24px', left:'24px', zIndex:50, width:'42px', height:'42px', borderRadius:'50%', cursor:'pointer', border:'none', background:B.grad, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:B.shadow }}>
          <ChevronDown style={{ color:B.white, width:'16px', height:'16px', transform:'rotate(180deg)' }}/>
        </button>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
//  ROOT
// ════════════════════════════════════════════════════════════════
const LumineWebsite = () => {
  const [niche, setNiche] = useState(null);

  if (!niche) {
    return (
      <ErrorBoundary>
        <SecurityGuard/>
        <NicheModal onSelect={setNiche}/>
      </ErrorBoundary>
    );
  }

  if (niche === 'telecom') {
    return (
      <ErrorBoundary>
        <TelecomSite onSwitch={() => setNiche(null)}/>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <IluminacaoSite onSwitch={() => setNiche(null)}/>
    </ErrorBoundary>
  );
};

export default LumineWebsite;